import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { LaunchLanguageCode } from '@/lib/translation/launchLanguages';
import type {
  ChatTranslationEntry,
  ChatTranslationsByMessage,
} from '@/lib/chat/chatTranslation';

export type ParticipantChatMessage = {
  id: string;
  message: string;
  timestamp: number;
  from: {
    identity: string;
    name: string;
    isOfficer: boolean;
  };
};

export interface ParticipantChatState {
  messages: ParticipantChatMessage[];
  /**
   * Translations of INCOMING messages into THIS reader's language, keyed by
   * message id.
   *
   * ⚠️ SEPARATE FROM `messages` ON PURPOSE. `message.message` is the text
   * exactly as sent and there is no code path that writes to it, so a
   * translation cannot overwrite what somebody typed. Chat is persisted into
   * the call archive and the incident report; a translation written in place
   * would put words in a caller's mouth in a record that outlives the call.
   *
   * ⚠️ NOT shared across clients. The same message carries a different id on the
   * sender's screen and the receiver's, so these keys are meaningful only in
   * the browser that produced them.
   */
  translations: ChatTranslationsByMessage;
}

const initialState: ParticipantChatState = {
  messages: [],
  translations: {},
};

/**
 * Is this response still wanted?
 *
 * A reader who changes language mid-call has an in-flight request for the OLD
 * one. Letting it land would overwrite a fresh translation with a stale-language
 * one — and because the store is also what decides whether to request a
 * translation, that would immediately trigger a second paid request for the
 * language we already had. So a response is applied only when it matches the
 * language the pending entry was opened for.
 */
function responseIsStillWanted(
  translations: ChatTranslationsByMessage,
  messageId: string,
  targetLanguage: LaunchLanguageCode,
): boolean {
  const pending = translations[messageId];
  // No entry at all means nothing opened this request — a response we never
  // asked for is not applied.
  if (!pending) return false;
  return pending.targetLanguage === targetLanguage;
}

const participantChatSlice = createSlice({
  name: 'participantChat',
  initialState,
  reducers: {
    addParticipantChatMessage: (state, action: PayloadAction<ParticipantChatMessage>) => {
      // Prevent duplicates based on ID
      const existingIndex = state.messages.findIndex(
        (m) => m.id === action.payload.id
      );

      if (existingIndex === -1) {
        state.messages.push(action.payload);
      }
    },

    setParticipantChatMessages: (state, action: PayloadAction<ParticipantChatMessage[]>) => {
      state.messages = action.payload;
    },

    clearParticipantChatMessages: (state) => {
      state.messages = [];
      // Translations are meaningless without the messages they belong to, and
      // leaving them would let a recycled id inherit another message's text.
      state.translations = {};
    },

    /** A request has been sent for this message, into this language. */
    chatTranslationRequested: (
      state,
      action: PayloadAction<{ messageId: string; targetLanguage: LaunchLanguageCode }>,
    ) => {
      const { messageId, targetLanguage } = action.payload;
      state.translations[messageId] = {
        state: 'pending',
        targetLanguage,
        text: null,
        sourceLanguage: null,
        reason: null,
        numbersVerified: null,
      };
    },

    /** A translation arrived. */
    chatTranslationReceived: (
      state,
      action: PayloadAction<{
        messageId: string;
        targetLanguage: LaunchLanguageCode;
        text: string | null;
        sourceLanguage: LaunchLanguageCode | null;
        sourceIsTarget: boolean;
        numbersVerified: boolean | null;
      }>,
    ) => {
      const { messageId, targetLanguage, text, sourceLanguage, sourceIsTarget, numbersVerified } =
        action.payload;
      if (!responseIsStillWanted(state.translations, messageId, targetLanguage)) return;

      // Trust the TEXT over the FLAG, matching the route: a response carrying a
      // translation has translated something, whatever `source_is_target` says.
      const entry: ChatTranslationEntry = text
        ? {
            state: 'translated',
            targetLanguage,
            text,
            sourceLanguage,
            reason: null,
            numbersVerified,
          }
        : {
            state: sourceIsTarget ? 'source_is_target' : 'unavailable',
            targetLanguage,
            text: null,
            sourceLanguage,
            reason: sourceIsTarget ? null : 'no_text',
            numbersVerified: null,
          };
      state.translations[messageId] = entry;
    },

    /**
     * The request failed. The reader is TOLD — this is never left as `pending`,
     * because a translation that says "translating" for the rest of the call is
     * the silent degradation the badge vocabulary exists to prevent.
     */
    chatTranslationFailed: (
      state,
      action: PayloadAction<{
        messageId: string;
        targetLanguage: LaunchLanguageCode;
        reason: string;
      }>,
    ) => {
      const { messageId, targetLanguage, reason } = action.payload;
      if (!responseIsStillWanted(state.translations, messageId, targetLanguage)) return;

      state.translations[messageId] = {
        state: 'unavailable',
        targetLanguage,
        text: null,
        sourceLanguage: null,
        reason,
        numbersVerified: null,
      };
    },
  },
});

export const {
  addParticipantChatMessage,
  setParticipantChatMessages,
  clearParticipantChatMessages,
  chatTranslationRequested,
  chatTranslationReceived,
  chatTranslationFailed,
} = participantChatSlice.actions;

export default participantChatSlice.reducer;
